import { Layout } from 'components/layout';
import { CardsSkeleton } from 'components/slider/cards/medium';

import { CustomMetadata } from 'components/metadata';
import { CardsMediumCarousel } from 'components/slider/embla-home/card';
import { Title } from 'components/title';
import { Analytics } from 'constants/analytics';
import { allMetadata } from 'constants/metadata';
import { Routes } from 'constants/routes';
import {
  ICasino,
  IExtendedCollection,
  INews,
  IProvider,
  ISlot,
  ISlotReview,
} from 'constants/types';
import { getCollectionIdentifier } from 'utils/slug';
import { getToken } from 'utils/storage';
import { useEffect, useMemo, useState } from 'react';
import { LoginWithOneTap } from '../auth';
import { CreateAccount } from './create-account';
import { HomeDescriptionHTML } from './description/html';
import { News } from './news';
import { Search } from './search';
import styles from './styles.module.css';
import { TableReviewSlot } from './table-review-slot';

const EMBLA_OPTIONS = {
  align: 'start' as const,
  dragFree: false,
  startIndex: 0,
};

export const CasinosLine = ({
  collection,
}: {
  collection?: IExtendedCollection;
}) => {
  return (
    <div className={styles.carousel}>
      <CardsMediumCarousel
        headerId={Analytics.home.openCollectionCard + `-${collection?.name}`}
        type="casino"
        description={collection?.description}
        name={collection?.name}
        count={collection?.count ?? '0'}
        slides={collection?.casinos ?? []}
        url={
          collection?.customUrl ??
          Routes.casinoCollections(getCollectionIdentifier(collection ?? {}))
        }
        options={EMBLA_OPTIONS}
      />
    </div>
  );
};

export const SlotsLine = ({
  slots,
  count,
  name,
}: {
  slots: ISlot[];
  count: number;
  name: string;
}) => {
  if (!slots?.length) {
    return <CardsSkeleton />;
  }

  return (
    <div className={styles.carousel}>
      <CardsMediumCarousel
        headerId={Analytics.home.openCollectionCard}
        type="slot"
        name={name}
        count={count}
        slides={slots}
        url={Routes.allSlots}
        options={EMBLA_OPTIONS}
      />
    </div>
  );
};

export const ProvidersLine = ({
  providers,
  count,
}: {
  providers: IProvider[];
  count: number;
}) => {
  return (
    <div className={styles.carousel}>
      <CardsMediumCarousel
        type="provider"
        name={'All slots Providers'}
        count={count}
        slides={providers?.length ? providers : []}
        url={Routes.providers}
        options={EMBLA_OPTIONS}
      />
    </div>
  );
};

export type Props = {
  news: INews[];
  allSlots: ISlot[];
  allProviders: IProvider[];
  allCasinos: ICasino[];
  latestSlotReviews: ISlotReview[];
  lastSlotReviewsCount: number;
  allSlotsCount: number;
  allProvidersCount: number;
  allCasinosCount: number;
};

export const MainPage = ({
  news,
  allSlots,
  allProviders,
  latestSlotReviews,
  lastSlotReviewsCount,
  allSlotsCount,
  allCasinos,
  allCasinosCount,
  allProvidersCount,
}: Props) => {
  const allCasinosCollection = useMemo(
    () => ({
      _id: 'all-casinos',
      slots_ids: [] as string[],
      casinos_ids: [] as string[],
      customUrl: Routes.allCasinos,
      casinos: allCasinos,
      name: 'Casino Ratings',
      count: allCasinosCount?.toString(),
    }),
    [allCasinos, allCasinosCount]
  );

  const [showOneTap, setShowOneTap] = useState(false);

  useEffect(() => {
    setShowOneTap(!getToken());
  }, []);

  return (
    <div>
      <CustomMetadata metadata={allMetadata.home} />
      <Layout bgVariant={'home'} hasFooter isSearchInputMobileVisible={false}>
        <div className={styles.container}>
          {showOneTap ? <LoginWithOneTap /> : null}
          <div className={styles.title}>
            <Title
              variant="home"
              text={
                'Check RTP before the gameplay. \nChoose online casino with the max RTP'
              }
              description={
                'RTP is the Return to Player percentage from a casino, which is the most important factor in gaming: the higher the RTP, the better your chances of winning'
              }
              gap={20}
            />
            <div className={styles.cup_image} />
          </div>
          <Search
            variant={'home'}
            placeholder="Check out the RTP online casino..."
            rounded
          />
          <div className={styles.trustedcasinos_container}>
            <CasinosLine collection={allCasinosCollection} />
          </div>
          <CreateAccount />
          <News
            title={'Casino News'}
            data={news}
            count={news.length}
            arrow
            variant={'exetended'}
            hasIcon
            headerIsSmallName
          />
          <SlotsLine
            slots={allSlots}
            count={allSlotsCount}
            name={'Slots RTP'}
          />
          <TableReviewSlot
            data={latestSlotReviews}
            count={lastSlotReviewsCount}
          />
          <ProvidersLine providers={allProviders} count={allProvidersCount} />
          <HomeDescriptionHTML />
        </div>
      </Layout>
    </div>
  );
};
